/**
 * Recording cues — two short tones, so the rep knows capture began and ended
 * without having to watch the button.
 *
 * Synthesised with WebAudio rather than shipped as audio files: two beeps do
 * not justify binary assets in the bundle, a fetch that can fail, or a decode
 * that can race the recording it is announcing.
 *
 * RISING (low → high) = started, FALLING (high → low) = stopped. The direction
 * carries the meaning; two identical beeps would tell you something happened but
 * not which thing.
 *
 * Both are called from a click handler, so the page always has the user gesture
 * an AudioContext needs. Everything is wrapped: a browser that blocks or lacks
 * WebAudio must lose the beep, never the recording.
 */

// One context, lazily made and reused — browsers cap how many you may create,
// and a per-beep context leaks until garbage collected.
let ctx = null;

const audioContext = () => {
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx || ctx.state === "closed") ctx = new Ctor();
  // Autoplay policy parks a context created before the first gesture.
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
};

/**
 * @param {number[]} steps  frequencies in Hz, played in order
 */
const chirp = (steps) => {
  try {
    const ac = audioContext();
    if (!ac) return;
    const each = 0.09;
    const gain = ac.createGain();
    gain.connect(ac.destination);
    // Ramped, never a bare start/stop: a square-edged gate on a sine clicks
    // audibly, which sounds like a fault rather than a cue.
    gain.gain.setValueAtTime(0.0001, ac.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, ac.currentTime + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + each * steps.length);

    steps.forEach((hz, i) => {
      const osc = ac.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(hz, ac.currentTime + i * each);
      osc.connect(gain);
      osc.start(ac.currentTime + i * each);
      osc.stop(ac.currentTime + (i + 1) * each);
    });
  } catch {
    // A missing beep is not worth surfacing.
  }
};

export const playStartCue = () => chirp([660, 990]);
export const playStopCue = () => chirp([990, 660]);
